import WarningIcon from "@mui/icons-material/Warning";
import {
	Box,
	Button,
	Divider,
	Grid,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	useGetAdminCompanionAppConfigQuery,
	useUpdateAdminCompanionAppConfigMutation,
} from "../../features/companion-app/companionAppApiSlice";

const timocomKeys = [
	"TIMOCOM_DASHBOARDS_URL",
	"TIMOCOM_LOGIN_URL",
	"TIMOCOM_AFTER_AUTH_ELEMENT_SELECTOR",
	"GUIDE_SELECTOR",
	"GUIDE_CLOSE_BUTTON_SELECTOR",
	"COOKIE_CONSENT_SELECTOR",
	"COOKIE_CONSENT_DECLINE_BUTTON_SELECTOR",
	"MESSENGER_STORAGE_KEY",
	"LOGIN_USERNAME_INPUT_SELECTOR",
	"LOGIN_PASSWORD_INPUT_SELECTOR",
	"LOGIN_SUBMIT_BUTTON_SELECTOR",
	"LOGIN_2FA_SELECTOR",
	"LOGIN_2FA_REMEMBER_ME_SELECTOR",
	"LOGIN_ERROR_MESSAGE_SELECTOR",
	"REMOVE_OFFER_EDIT_URL",
	"REMOVE_OFFER_EDIT_CONTAINER_SELECTOR",
	"REMOVE_CLOSE_BUTTON_SELECTOR",
	"REMOVE_REMOVE_OPTION_SELECTOR",
	"REMOVE_CONFIRM_BUTTON_SELECTOR",
	"REMOVE_OPTIONAL_SURVEY_SELECTOR",
	"REMOVE_OPTIONAL_SURVEY_RADIO_SELECTOR",
	"REMOVE_OPTIONAL_SURVEY_BUTTON_SELECTOR",
	"ADD_NEW_OFFER_BUTTON_SELECTOR",
	"ADD_NEW_OFFER_FORM_SELECTOR",
	"ADD_SUBMIT_BUTTON_SELECTOR",
	"ADD_POST_API_ENDPOINT_PATHNAME",
	"ADD_LOADING_COUNTRY_SELECTOR",
	"ADD_UNLOADING_COUNTRY_SELECTOR",
	"ADD_LOADING_COUNTRY_POPPER_SELECTOR",
	"ADD_UNLOADING_COUNTRY_POPPER_SELECTOR",
	"ADD_LOADING_ADDRESS_SELECTOR",
	"ADD_UNLOADING_ADDRESS_SELECTOR",
	"ADD_LOADING_ADDRESS_POPPER_SELECTOR",
	"ADD_UNLOADING_ADDRESS_POPPER_SELECTOR",
	"ADD_ADDRESS_GEO_API_ENDPOINT_PATHNAME",
	"ADD_ADDRESS_GEO_API_ENDPOINT_PARAM",
	"ADD_LOADING_ADDRESS_FIRST_OPTION_SELECTOR",
	"ADD_UNLOADING_ADDRESS_FIRST_OPTION_SELECTOR",
	"ADD_LOADING_DATE_START_SELECTOR",
	"ADD_LOADING_DATE_END_SELECTOR",
	"ADD_UNLOADING_DATE_START_SELECTOR",
	"ADD_UNLOADING_DATE_END_SELECTOR",
	"ADD_LOADING_TIME_START_SELECTOR",
	"ADD_LOADING_TIME_END_SELECTOR",
	"ADD_UNLOADING_TIME_START_SELECTOR",
	"ADD_UNLOADING_TIME_END_SELECTOR",
	"ADD_VEHICLE_TYPE_INPUT_SELECTOR",
	"ADD_VEHICLE_TYPE_POPPER_SELECTOR",
	"ADD_VEHICLE_TYPE_OPTION_SELECTOR",
	"ADD_VEHICLE_BODY_INPUT_SELECTOR",
	"ADD_VEHICLE_BODY_POPPER_SELECTOR",
	"ADD_VEHICLE_BODY_OPTION_SELECTOR",
	"ADD_WEIGHT_SELECTOR",
	"ADD_LENGTH_SELECTOR",
	"ADD_TYPE_OF_GOODS_SELECTOR",
	"ADD_TYPE_OF_GOODS_SUGGESTION_SELECTOR",
	"ADD_PUBLIC_NOTE_SELECTOR",
	"ADD_CURRENCY_SELECTOR",
	"ADD_CURRENCY_POPPER_SELECTOR",
	"ADD_PRICE_INPUT_SELECTOR",
	"ADD_PAYMENT_DAYS_INPUT_SELECTOR",
	"ADD_ADDITIONAL_REQ_INPUT_SELECTOR",
	"ADD_ADDITIONAL_REQ_POPPER_SELECTOR",
	"ADD_ADDITIONAL_REQ_OPTION_SELECTOR",
	"ADD_BLACKLIST_BUTTON_SELECTOR",
	"ADD_AI_UPLOAD_SELECTOR",
	"ADD_AI_FILE_SUBMIT_SELECTOR",
	"ADD_AI_FINAL_SUBMIT_SELECTOR",
	"ADD_AI_ERROR_SELECTOR",
	"ADD_AI_CLOSE_SELECTOR",
	"REFRESH_OFFER_EDIT_URL",
	"REFRESH_SUBMIT_BUTTON_SELECTOR",
	"CS_DATE_FORMAT",
	"DA_DATE_FORMAT",
	"DE_DATE_FORMAT",
	"EN_DATE_FORMAT",
	"ES_DATE_FORMAT",
	"FR_DATE_FORMAT",
	"HR_DATE_FORMAT",
	"IT_DATE_FORMAT",
	"LT_DATE_FORMAT",
	"HU_DATE_FORMAT",
	"NL_DATE_FORMAT",
	"NO_DATE_FORMAT",
	"PL_DATE_FORMAT",
	"PT_DATE_FORMAT",
	"RU_DATE_FORMAT",
	"RO_DATE_FORMAT",
	"SL_DATE_FORMAT",
	"SK_DATE_FORMAT",
	"SR_DATE_FORMAT",
	"FI_DATE_FORMAT",
	"SV_DATE_FORMAT",
	"TR_DATE_FORMAT",
	"EL_DATE_FORMAT",
	"BG_DATE_FORMAT",
];

const transKeys = [
	"TRANS_DASHBOARDS_URL",
	"TRANS_LOGIN_URL",
	"TRANS_AFTER_AUTH_ELEMENT_SELECTOR",
	"LOGIN_USERNAME_INPUT_SELECTOR",
	"LOGIN_PASSWORD_INPUT_SELECTOR",
	"LOGIN_SUBMIT_BUTTON_SELECTOR",
	"LOGIN_CAPTCHA_SELECTOR",
	"LOGIN_CAPTCHA_FINISHED_SELECTOR",
	"LOGIN_2FA_SELECTOR",
	"LOGIN_ERROR_MESSAGE_SELECTOR",
	"REMOVE_SEARCH_INPUT_SELECTOR",
	"REMOVE_CLOSE_BUTTON_SELECTOR",
	"ADD_NEW_OFFER_BUTTON_SELECTOR",
	"ADD_NEW_OFFER_FALLBACK_BUTTON_SELECTOR",
	"ADD_FREIGHT_FORM_MODAL_SELECTOR",
	"ADD_PUBLISH_BUTTON_SELECTOR",
	"ADD_POST_API_ENDPOINT_PATHNAME",
	"ADD_ADD_ANOTHER_FREIGHT_CHECKBOX_SELECTOR",
	"ADD_ADD_ANOTHER_FREIGHT_LABEL_SELECTOR",
	"ADD_CLEAR_FORM_BUTTON_SELECTOR",
	"ADD_FTL_SELECTOR",
	"ADD_LTL_SELECTOR",
	"ADD_LOADING_ADDRESS_SEARCH_SELECTOR",
	"ADD_UNLOADING_ADDRESS_SEARCH_SELECTOR",
	"ADD_ADDRESS_SEARCH_INPUT_SELECTOR",
	"ADD_ADDRESS_GEO_API_ENDPOINT_PATHNAME",
	"ADD_ADDRESS_FIRST_OPTION_SELECTOR",
	"ADD_ADDRESS_MANUAL_HELPER_SELECTOR",
	"ADD_LOADING_DATE_DAY_SELECTOR",
	"ADD_UNLOADING_DATE_DAY_SELECTOR",
	"ADD_LOADING_DATE_RANGE_FROM_SELECTOR",
	"ADD_UNLOADING_DATE_RANGE_FROM_SELECTOR",
	"ADD_LOADING_DATE_RANGE_TO_SELECTOR",
	"ADD_UNLOADING_DATE_RANGE_TO_SELECTOR",
	"ADD_LOADING_DATE_TO_RANGE_SWITCH_SELECTOR",
	"ADD_UNLOADING_DATE_TO_RANGE_SWITCH_SELECTOR",
	"ADD_DATE_NEXT_DAY_BUTTON_SELECTOR",
	"ADD_LOADING_TIME_TO_RANGE_SWITCH_SELECTOR",
	"ADD_UNLOADING_TIME_TO_RANGE_SWITCH_SELECTOR",
	"ADD_LOADING_TIME_FROM_SELECTOR",
	"ADD_UNLOADING_TIME_FROM_SELECTOR",
	"ADD_LOADING_TIME_TO_SELECTOR",
	"ADD_UNLOADING_TIME_TO_SELECTOR",
	"ADD_LOADING_TIME_SINGLE_SELECTOR",
	"ADD_UNLOADING_TIME_SINGLE_SELECTOR",
	"ADD_LOADING_TIME_RANGE_START_SELECTOR",
	"ADD_UNLOADING_TIME_RANGE_START_SELECTOR",
	"ADD_LOADING_TIME_RANGE_END_SELECTOR",
	"ADD_UNLOADING_TIME_RANGE_END_SELECTOR",
	"ADD_VEHICLE_TYPE_OPTION_CHECKBOX_SELECTOR",
	"ADD_VEHICLE_BODY_OPEN_TRIGGER_SELECTOR",
	"ADD_VEHICLE_BODY_OPTIONS_CONTAINER_SELECTOR",
	"ADD_VEHICLE_BODY_OPTION_SELECTOR",
	"ADD_WEIGHT_SELECTOR",
	"ADD_LENGTH_SELECTOR",
	"ADD_PUBLIC_NOTE_SELECTOR",
	"ADD_PRICE_TRIGGER_SELECTOR",
	"ADD_PRICE_OPTIONS_CONTAINER_SELECTOR",
	"ADD_PRICE_EUR_OPTION_SELECTOR",
	"ADD_PRICE_INPUT_SELECTOR",
	"ADD_ADDITIONAL_REQ_REVEAL_BUTTON_SELECTOR",
	"ADD_ADDITIONAL_REQ_OPEN_TRIGGER_SELECTOR",
	"ADD_ADDITIONAL_REQ_OPTIONS_CONTAINER_SELECTOR",
	"ADD_ADDITIONAL_REQ_OPTION_SELECTOR",
];

export const SuperAdminConfig = () => {
	const { t } = useTranslation();

	const { data, isLoading, isFetching } = useGetAdminCompanionAppConfigQuery();
	const [updateConfig, { isLoading: isSaving }] =
		useUpdateAdminCompanionAppConfigMutation();

	const {
		handleSubmit,
		control,
		formState: { errors },
		reset,
	} = useForm({
		shouldFocusError: true,
		defaultValues: {},
	});

	const defaultConfig = useMemo(() => {
		return data?.defaultConfig || { timocom: {}, trans: {} };
	}, [data?.defaultConfig]);

	const loadedConfig = useMemo(() => {
		return data?.config || null;
	}, [data?.config]);

	useEffect(() => {
		if (loadedConfig) {
			reset(loadedConfig);
		}
	}, [loadedConfig, reset]);

	const handleSaveConfig = async (formData) => {
		const response = await updateConfig({ config: formData }).unwrap();
		if (response?.config) {
			reset(response.config);
		}
	};

	const renderConfigFields = (prefix, keys) => {
		return (
			<Grid container spacing={1.5}>
				{keys.map((key) => {
					const name = `${prefix}.${key}`;
					const isLikelyUrl = key.endsWith("URL");
					const defaultValue = defaultConfig?.[prefix]?.[key];
					const hasDynamicPlaceholder =
						typeof defaultValue === "string" &&
						(defaultValue.includes("<option>") ||
							defaultValue.includes("<selector>"));

					return (
						<Grid key={name} size={12}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								{hasDynamicPlaceholder && (
									<Tooltip
										title={
											"Musi zawierać dynamiczny placeholder <option> lub <selector>"
										}
									>
										<WarningIcon color="error" />
									</Tooltip>
								)}
								<Controller
									name={name}
									control={control}
									defaultValue={""}
									render={({ field }) => (
										<TextField
											{...field}
											value={field.value ?? ""}
											label={key}
											fullWidth
											size="small"
											variant="outlined"
											type={isLikelyUrl ? "url" : "text"}
											placeholder={
												typeof defaultValue === "string"
													? defaultValue
													: undefined
											}
											error={Boolean(errors?.[prefix]?.[key])}
											helperText={errors?.[prefix]?.[key]?.message}
										/>
									)}
								/>
							</Box>
						</Grid>
					);
				})}
			</Grid>
		);
	};

	return (
		<Box>
			<Box
				component="form"
				noValidate
				onSubmit={handleSubmit(handleSaveConfig)}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box>
						<Typography variant="h6">TIMOCOM</Typography>
						<Divider sx={{ mt: 1.5 }} />
					</Box>
					{renderConfigFields("timocom", timocomKeys)}

					<Box sx={{ mt: 2 }}>
						<Typography variant="h6">TRANS</Typography>
						<Divider sx={{ mt: 1.5 }} />
					</Box>
					{renderConfigFields("trans", transKeys)}

					<Box
						sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
					>
						<Button
							disabled={isLoading || isFetching || isSaving}
							type="button"
							variant="outlined"
							onClick={() => reset(loadedConfig || {})}
						>
							{t("reset")}
						</Button>
						<Button
							disabled={isLoading || isFetching}
							loading={isSaving}
							type="submit"
							variant="contained"
						>
							{t("save")}
						</Button>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
